import submitNewShippingAddressForm from "@iewccouk/Store/Handlers/MyAccount/RequestNewShippingAddress";
import { setGA4Event } from "@iewccouk/Store/Handlers/Utilities/CustomTracking";
import { CustomState } from "@iewccouk/Store/Reducers";
import { scrollToFirstError } from "@iewccouk/Utilities/formValidationScroll";
import { mediaQueriesBP } from "@iewccouk/Utilities/variables";
import ShippingAddressForm, { ShippingAddressFormStyles } from "@iewccouk/Widgets/MyAccount/CustomShippingAddressForm";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import loadAddressFields from "@insite/client-framework/Store/Data/AddressFields/Handlers/LoadAddressFields";
import { getBillToState, getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import {
    getCountriesDataView,
    getCurrentCountries,
} from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries, {
    loadCurrentCountriesParameter,
} from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CheckoutShippingFormContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import BillingAddressInfoDisplay, {
    BillingAddressInfoDisplayStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/BillingAddressInfoDisplay";
import Button, { ButtonProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import parse from "html-react-parser";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export const enum fields {
    topContent = "topContent",
    bottomContent = "bottomContent",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.topContent]: string;
        [fields.bottomContent]: string;
    };
}

const mapStateToProps = (state: CustomState) => {
    return {
        billToState: getCurrentBillToState(state),
        shipToState: getCurrentShipToState(state),
        countries: getCurrentCountries(state),
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        countriesDataView: getCountriesDataView(state, loadCurrentCountriesParameter),
        customNewShippingAddressForm: state.customNewShippingAddressForm,
        session: state.context.session,
        isAuthenticated: state.context.session && (state.context.session.isAuthenticated || state.context.session.rememberMe) && !state.context.session.isGuest,
        billTo: getBillToState(state, state.context?.session?.billToId).value,
    };
};

const mapDispatchToProps = {
    loadAddressFields,
    loadCurrentCountries,
    loadCurrentShipTo,
    loadCurrentBillTo,
    submitNewShippingAddressForm,
};

export type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface AccountManagerStyles {
    container?: GridContainerProps;
    billToInfo?: GridItemProps;
    shipToInfo?: GridItemProps;
    shippingAddressForm?: ShippingAddressFormStyles;
    billingAddress?: BillingAddressInfoDisplayStyles;
    submitButton?: ButtonProps;
    loadingOverlay?: LoadingOverlayProps;
    topContent?: TypographyProps;
    bottomContent?: TypographyProps;
}

export const accountManagerStyles: AccountManagerStyles = {
    container: {
        gap: 0,
        css: css`
            margin-top: 40px;
        `,
    },

    billToInfo: {
        width: [12, 12, 5, 5, 5],
        css: css`
            flex-direction: column;

            ${mediaQueriesBP.maxMD} {
                margin-bottom: 20px !important;
            }
        `,
    },

    shipToInfo: {
        width: [12, 12, 7, 7, 7],
        css: css`
            flex-direction: column;
        `,
    },

    billingAddress: {
        headingGridItem: {
            css: css`
                display: none;
            `,
        },
    },

    shippingAddressForm: {
        formFields: {
            firstNameGridItem: {
                width: 12,
            },

            lastNameGridItem: {
                width: 12,
            },

            companyNameGridItem: {
                width: 12,
            },

            attentionGridItem: {
                width: 12,
            },

            stateGridItem: {
                width: 12,
            },

            postalCodeGridItem: {
                width: 12,
            },

            phoneGridItem: {
                width: 12,
            },

            faxGridItem: {
                width: 12,
            },
        },
    },

    submitButton: {
        css: css`
            margin-top: 20px;
            margin-left: auto;

            ${mediaQueriesBP.maxMD} {
                width: 100%;
            }
        `,
    },

    topContent: {
        size: 14,
        css: css`
            margin-top: 20px;
        `,
    },

    bottomContent: {
        size: 14,
        css: css`
            margin-top: 20px;
        `,
    },
};
export const styles = accountManagerStyles;

export const RequestNewShippingAddress = ({
    billToState,
    shipToAddressFields,
    countries,
    loadAddressFields,
    loadCurrentCountries,
    loadCurrentBillTo,
    loadCurrentShipTo,
    customNewShippingAddressForm,
    submitNewShippingAddressForm,
    session,
    fields,
    billTo,
    isAuthenticated,
}: Props) => {
    useEffect(() => {
        loadAddressFields();
        loadCurrentCountries();
        loadCurrentBillTo();
        loadCurrentShipTo();
    }, []);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);
    const toasterContext = useContext(ToasterContext);

    // Create form context with validators
    const formContext = useMemo(
        () => ({
            validators: {} as { [key: string]: (() => Promise<boolean>) | undefined },
            formSubmitAttempted,
            isRedirecting: false,
            isValidatingCart: false,
        }),
        [formSubmitAttempted],
    );

    if (!billToState.value || !shipToAddressFields || !countries || !session.isAuthenticated) {
        return null;
    }

    const handleSubmit = async () => {
        // Capture validators before setting formSubmitAttempted to avoid stale reference
        const validatorKeys = Object.keys(formContext.validators);
        const validators = validatorKeys.map(key => formContext.validators[key]).filter(Boolean);

        // Set submit attempted flag
        setFormSubmitAttempted(true);

        // Run all validators
        const validatorPromises = validators.map(async validator => {
            try {
                return await validator!();
            } catch (error) {
                return false;
            }
        });

        const results = await Promise.all(validatorPromises);
        const isValid = results.every(result => result === true);

        if (!isValid) {
            // Scroll to first error field
            setTimeout(() => {
                scrollToFirstError("newShippingAddressForm", {
                    offset: 120,
                    behavior: "smooth",
                });
            }, 150);
            // Reset formSubmitAttempted to allow retry
            setFormSubmitAttempted(false);
            return;
        }

        setIsSubmitting(true);
        submitNewShippingAddressForm({
            additionalData: {
                customerSequence: billToState.value.customerSequence,
                customerNumber: billToState.value.customerNumber,
                customerName: billToState.value.customerName,
                stateProvinceBilling: billToState.value.state?.name || "",
                zipPostalCodeBilling: billToState.value.postalCode,
                loginEmailUser: session?.email,
                lastNameUser: session?.lastName,
                firstNameUser: session?.firstName,
            },
            onSuccess: res => {
                setIsSubmitting(false);
                toasterContext.addToast({
                    body: siteMessage("CreateNewShippingAddress_SuccessMessage"),
                    messageType: "success",
                });

                let sapAccountNumber = "";
                if (isAuthenticated) {
                    sapAccountNumber = isAuthenticated && billTo ? billTo.customerNumber.replace(/^0+/, "") : "__IS_LOADING__";
                }

                const formID = "newShippingAddressForm";
                const accountNumber = sapAccountNumber;
                const formType = "New Shipping Address Form";
                setGA4Event([], "formSubmissionSuccess", { formType, formID, accountNumber });
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            },

            onError: () => {
                setIsSubmitting(false);
                setFormSubmitAttempted(false);
            },
        });
    };

    return (
        <>
            <LoadingOverlay {...styles.loadingOverlay} loading={isSubmitting}>
                <Typography {...styles.topContent}>{parse(fields.topContent, parserOptions)}</Typography>
                <CheckoutShippingFormContext.Provider value={formContext}>
                    <div id="newShippingAddressForm">
                        <GridContainer {...styles.container}>
                            <GridItem {...styles.billToInfo}>
                                <Typography variant="h2">{translate("Checkout Billing Information")}</Typography>
                                <BillingAddressInfoDisplay
                                    billTo={billToState.value}
                                    onEdit={() => {}}
                                    extendedStyles={styles.billingAddress}
                                />
                            </GridItem>
                            <GridItem {...styles.shipToInfo}>
                                {countries && shipToAddressFields && (
                                    <>
                                        <Typography variant="h2">
                                            {translate("Checkout Shipping Information")}
                                        </Typography>
                                        <ShippingAddressForm
                                            countries={countries}
                                            fieldDisplay={shipToAddressFields}
                                            extendedStyles={styles.shippingAddressForm}
                                        />
                                        <Typography {...styles.bottomContent}>
                                            {parse(fields.bottomContent, parserOptions)}
                                        </Typography>
                                        <Button
                                            {...styles.submitButton}
                                            onClick={handleSubmit}
                                            variant="primary"
                                            type="submit"
                                        >
                                            {translate("Submit")}
                                        </Button>
                                    </>
                                )}
                            </GridItem>
                        </GridContainer>
                    </div>
                </CheckoutShippingFormContext.Provider>
            </LoadingOverlay>
        </>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RequestNewShippingAddress as any) as any,
    definition: {
        displayName: "Request New Shipping Address",
        group: "Verndale Box" as any,
        icon: "puzzle-piece",
        fieldDefinitions: [
            {
                name: fields.topContent,
                displayName: "Top Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.bottomContent,
                displayName: "Bottom Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
    },
};
export default widgetModule;
